import { useState } from "react";
import { Badge } from "react-bootstrap";
import { useSharedState } from "../../../../app/sharedProperty";
import { Icon, IconDefinitions, StatusBadge } from "../../../../components";
import { FilterToggle } from "../../../../components/filterToggle";
import { GaugeConfig, PeakSignalModel, PeakSignalType, PeakSignalUpdateModel } from "../../../../data/entities";
import { Roles } from "../../../../models";
import { UserService } from "../../../../services";
import { deriveFlwGaugeConfig, getGaugeLevel, rawToPercent } from "../../helper/helpers";
import { DeletePeakSignalModal } from "./DeletePeakSignalModal";
import { EditPeakSignalModal } from "./EditPeakSignalModal";
import { SIGNAL_LABELS, formatNumber, formatTime } from "./peakSignalFormat";
import "./PeakSignalTable.scss";

type SignalFilter = "all" | PeakSignalType;

interface PeakSignalTableProps {
  peakSignals: PeakSignalModel[] | null | undefined;
  config: GaugeConfig | null;
  isLive?: boolean;
  scrollWithPage?: boolean;
  canEdit?: boolean;
  onUpdate?: (signal: PeakSignalModel, changes: PeakSignalUpdateModel) => void | Promise<void>;
  onDelete?: (signal: PeakSignalModel) => void | Promise<void>;
}

const TH_CLASS = "text-uppercase fw-semibold text-nowrap";

const sortTimestamp = (signal: PeakSignalModel) =>
  Date.parse(signal.startTime ?? signal.createdDate);

export function PeakSignalTable({ peakSignals, config, isLive = false, scrollWithPage = false, canEdit = false, onUpdate, onDelete }: PeakSignalTableProps) {
  const [signalFilter, setSignalFilter] = useState<SignalFilter>("all");
  const [selectedSignal, setSelectedSignal] = useState<PeakSignalModel | null>(null);
  const editShowState = useSharedState(false);
  const deleteShowState = useSharedState(false);
  const flwConfig = deriveFlwGaugeConfig(config);
  const signals = peakSignals ?? [];
  const isStaff = UserService.isInAnyRole([Roles.admin, Roles.staff, Roles.dataAnalyst]);
  const showActions = canEdit && (onUpdate != null || onDelete != null);

  const openEdit = (signal: PeakSignalModel) => {
    setSelectedSignal(signal);
    editShowState.setter(true);
  };

  const openDelete = (signal: PeakSignalModel) => {
    setSelectedSignal(signal);
    deleteShowState.setter(true);
  };

  const lfCount = signals.filter((s) => s.signalType === "LF").length;
  const lmaCount = signals.length - lfCount;
  
  // Only offer filtering when there is actually something to separate
  const showFilter = lfCount > 0 && lmaCount > 0;

  const positionMode = signals.length > 0 && signals.every((s) => s.startTime == null);

  const sorted = [...signals].sort((a, b) => {
    if (positionMode) {
      const diff = (a.startPosition ?? 0) - (b.startPosition ?? 0);
      return diff !== 0 ? diff : a.id - b.id;
    }
    const diff = sortTimestamp(b) - sortTimestamp(a);
    return diff !== 0 ? diff : b.id - a.id;
  });

  const visible =
    showFilter && signalFilter !== "all"
      ? sorted.filter((s) => s.signalType === signalFilter)
      : sorted;

  return (
    <div className="peak-signal-log">
      <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <span className="peak-signal-log__label text-uppercase fw-semibold">
            Peak Signal Log
          </span>
          {!showFilter && sorted.length > 0 && (
            <Badge bg="secondary" pill className="peak-signal-log__count">
              {sorted.length}
            </Badge>
          )}
        </div>
        {showFilter && (
          <FilterToggle
            options={[
              { key: "all", label: "All", count: signals.length },
              { key: "LF", label: "LF", count: lfCount },
              { key: "LMA", label: "LMA", count: lmaCount },
            ]}
            activeFilter={signalFilter}
            onChange={setSignalFilter}
          />
        )}
      </div>

      {sorted.length === 0 ? (
        <div className="text-center text-muted py-3">
          {isLive ? (
            <small className="d-flex align-items-center justify-content-center gap-2">
              <Icon iconName={IconDefinitions.circleCheck} className="text-success" />
              No peak signals detected — readings are within the normal range
            </small>
          ) : (
            <small>No peak signals were recorded for this inspection</small>
          )}
        </div>
      ) : (
        <div
          className={
            scrollWithPage
              ? "peak-signal-log__scroll peak-signal-log__scroll--expand"
              : "peak-signal-log__scroll table-responsive"
          }
        >
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th className={TH_CLASS}>Signal</th>
                <th className={TH_CLASS}>Severity</th>
                {isStaff && <th className={TH_CLASS}>Threshold %</th>}
                <th className={TH_CLASS}>{positionMode ? "Start Position" : "Start"}</th>
                <th className={TH_CLASS}>{positionMode ? "End Position" : "End"}</th>
                {showActions && <th className={`${TH_CLASS} text-end`} aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {visible.map((signal) => {
                const rowConfig = signal.signalType === "LF" ? config : flwConfig;
                const severity = getGaugeLevel(signal.peakValue, rowConfig);
                const gaugePercent = rowConfig ? rawToPercent(signal.peakValue, rowConfig) : null;
                const isOpen = !positionMode && signal.endTime == null;
                const peakLength =
                  signal.startPosition != null && signal.endPosition != null
                    ? signal.endPosition - signal.startPosition
                    : null;
                const rowClasses = [
                  "peak-signal-log__row",
                  isOpen && isLive && severity !== "none"
                    ? `peak-signal-log__row--active-${severity}`
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <tr key={`${signal.signalType}-${signal.id}`} className={rowClasses}>
                    <td>
                      <span className="fw-semibold">{signal.signalType}</span>
                      <div className="peak-signal-log__sub">{SIGNAL_LABELS[signal.signalType]}</div>
                    </td>
                    <td>
                      {severity === "critical" ? (
                        <StatusBadge variant="red" label="Alert" size="sm" />
                      ) : severity === "caution" ? (
                        <StatusBadge variant="amber" label="Warning" size="sm" />
                      ) : (
                        <StatusBadge variant="gray" label="Recorded" size="sm" />
                      )}
                    </td>
                    {isStaff && (
                      <td className="fw-semibold">
                        {gaugePercent != null ? `${Math.round(gaugePercent)}%` : "—"}
                      </td>
                    )}
                    <td>
                      {positionMode ? (
                        <div>{signal.startPosition != null ? formatNumber(signal.startPosition) : "—"}</div>
                      ) : (
                        <>
                          <div>{signal.startTime ? formatTime(signal.startTime) : "—"}</div>
                          {signal.startPosition != null && (
                            <div className="peak-signal-log__sub">
                              Pos {formatNumber(signal.startPosition)}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                    <td>
                      {positionMode ? (
                        <>
                          <div>{signal.endPosition != null ? formatNumber(signal.endPosition) : "—"}</div>
                          {peakLength != null && (
                            <div className="peak-signal-log__sub">
                              Length {formatNumber(peakLength)}
                            </div>
                          )}
                        </>
                      ) : isOpen ? (
                        isLive ? (
                          <span
                            className={`peak-signal-log__in-progress peak-signal-log__in-progress--${severity} d-inline-flex align-items-center fw-semibold`}
                          >
                            <span className="peak-signal-log__pulse d-inline-block rounded-circle" />
                            In progress
                          </span>
                        ) : (
                          "—"
                        )
                      ) : (
                        <>
                          <div>{formatTime(signal.endTime!)}</div>
                          {signal.endPosition != null && (
                            <div className="peak-signal-log__sub">
                              Pos {formatNumber(signal.endPosition)}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                    {showActions && (
                      <td className="text-end text-nowrap">
                        {onUpdate && (
                          <button
                            type="button"
                            className="btn btn-sm btn-link p-1"
                            title="Edit peak value"
                            onClick={() => openEdit(signal)}
                          >
                            <Icon iconName={IconDefinitions.edit} />
                          </button>
                        )}
                        {onDelete && (
                          <button
                            type="button"
                            className="btn btn-sm btn-link p-1 text-danger"
                            title="Delete peak signal"
                            onClick={() => openDelete(signal)}
                          >
                            <Icon iconName={IconDefinitions.delete} />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showActions && onUpdate && (
        <EditPeakSignalModal
          showState={editShowState}
          signal={selectedSignal}
          config={selectedSignal?.signalType === "LF" ? config : flwConfig}
          onSave={onUpdate}
        />
      )}
      {showActions && onDelete && (
        <DeletePeakSignalModal
          showState={deleteShowState}
          signal={selectedSignal}
          config={selectedSignal?.signalType === "LF" ? config : flwConfig}
          onConfirm={onDelete}
        />
      )}
    </div>
  );
}
